import React from 'react';
import Modal from '@amzn/meridian/modal';
import Column from '@amzn/meridian/column';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import Button from '@amzn/meridian/button';
import Alert from '@amzn/meridian/alert';
import * as AppStrings from 'src/localization/string-ids/app-strings';
import { buildCleanADirtyToteInput, CleanADirtyToteInput } from 'src/models/cleanADirtyTote';
import { cleanADirtyTote } from 'src/store/common-slices/cleanADirtyToteSlice';
import { AlertLevels } from 'src/models/alert-model';
import { UIAlertState } from 'src/store/common-slices/uiSlice';

interface CleanContainerConfirmModalStateProps {
    uiAlertState: UIAlertState;
    toteId: string;
    warehouseId: string;
    associate: string;
    locale: string;
}

interface CleanContainerConfirmModalDispatchProps {
    cleanADirtyTote: (input: CleanADirtyToteInput) => Promise<any>;
}

interface CleanContainerConfirmModalOwnProps {
    onClose: () => void;
    onCleanComplete: () => void;
}

type Props = CleanContainerConfirmModalStateProps & CleanContainerConfirmModalDispatchProps & CleanContainerConfirmModalOwnProps;

interface State {
    submitting: boolean;
    hasSubmitted: boolean;
}

class CleanContainerConfirmModal extends React.Component<Props, State> {

    constructor(props: Props) {
        super(props);
        this.state = {
            submitting: false,
            hasSubmitted: false
        };
    }

    private onConfirm = async () => {
        this.setState({ submitting: true, hasSubmitted: true });
        try {
            const result = await this.props.cleanADirtyTote(buildCleanADirtyToteInput(
                this.props.toteId, this.props.warehouseId, this.props.associate, this.props.locale));

            if (cleanADirtyTote.fulfilled.match(result)) {
                this.props.onCleanComplete();
                this.props.onClose();
            }
            // On failure, the modal stays open (no onClose call) so the associate can see the
            // error below and retry without re-scanning.
        } finally {
            this.setState({ submitting: false });
        }
    }

    private onCancel = () => {
        this.props.onClose();
    }

    public render() {
        return (
            <Modal title={AppStrings.CLEAN_CONTAINER_CONFIRM_MODAL_TITLE} open={true}
                   onClose={this.state.submitting ? undefined : this.onCancel}
                   width={'60vw'} data-testid="clean-container-confirm-modal">
                <Column spacingInset="400">
                    <Text data-testid="clean-container-confirm-message">
                        {AppStrings.CLEAN_CONTAINER_CONFIRM_MODAL_MESSAGE} {this.props.toteId}?
                    </Text>
                    {this.state.hasSubmitted && this.props.uiAlertState.alertLevel === AlertLevels.ERROR &&
                        <Alert type="error" size="medium" data-testid="clean-container-confirm-error">
                            {this.props.uiAlertState.alertMessageString}
                        </Alert>
                    }
                    <Row alignmentHorizontal="end">
                        <Button type="secondary" disabled={this.state.submitting} onClick={this.onCancel}
                                data-testid="clean-container-cancel-button">
                            {AppStrings.CLEAN_CONTAINER_CANCEL_BUTTON}
                        </Button>
                        <Button disabled={this.state.submitting} onClick={this.onConfirm}
                                data-testid="clean-container-confirm-button">
                            {AppStrings.CLEAN_CONTAINER_CONFIRM_BUTTON}
                        </Button>
                    </Row>
                </Column>
            </Modal>
        );
    }
}

export default CleanContainerConfirmModal;
